import "~/components/ui/labelled-radio.css";

import clsx from "clsx";
import React from "react";
import { FormattedMessage, type MessageDescriptor } from "react-intl";
import { Field, type WrappedFieldProps } from "redux-form";

import { LabelledRadio } from "~/components/ui/labelled-radio";

export function RecipeRadio({
  name,
  value,
  intlId,
}: {
  name: string;
  value: number;
  intlId: MessageDescriptor["id"];
}) {
  return (
    <Field
      name={name}
      component={RadioInput}
      intlId={intlId}
      radioValue={value}
      normalize={Number}
    />
  );
}

type RecipeRadioProps = React.ComponentPropsWithoutRef<typeof RecipeRadio>;
type RadioInputProps = Pick<RecipeRadioProps, "intlId"> & { radioValue: RecipeRadioProps["value"] };

function RadioInput({ intlId, radioValue, ...formProps }: WrappedFieldProps & RadioInputProps) {
  return (
    <LabelledRadio
      {...formProps.input}
      label={<FormattedMessage id={intlId} />}
      value={`${radioValue}`}
      checked={formProps.input.value == radioValue}
    />
  );
}

export function RecipeCheckbox<T extends MessageDescriptor["id"]>({
  name,
  value,
  intlId,
  ariaLabelledBy,
  onChange,
  renderLabel,
}: {
  name: string;
  value: number | undefined;
  intlId: T;
  ariaLabelledBy: string | string[];
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  renderLabel?: (intlId: T) => React.ReactNode;
}) {
  const inputId = `${name}-value-${value ?? ""}`;

  return (
    <label htmlFor={inputId} className="rd-option">
      <span className="rd-radio">
        <Field
          id={inputId}
          aria-labelledby={clsx(ariaLabelledBy, inputId)}
          name={name}
          component="input"
          type="checkbox"
          className="rd-radio__input"
          format={typeof value == "number" ? (v?: number) => v === value : undefined}
          normalize={typeof value == "number" ? (v: boolean) => (v ? value : null) : undefined}
          onChange={onChange}
        />
        <span className="rd-radio__face" aria-hidden="true">
          <span className="rd-radio__dot" />
        </span>
      </span>
      <span className="rd-option__text">
        {renderLabel ? renderLabel(intlId) : <FormattedMessage id={intlId} />}
      </span>
    </label>
  );
}
